// Survival sounds (2026-10-15), synthesised with WebAudio like client/src/sfx.ts, so there are no files: the Breath
// whispering (breathy filtered noise that rises and falls), the radio's crackle before a line, a soft tick when a
// timed action finishes, and a two-note blip when a moodle gets worse. Muted with the music (SOUND button / M).
import { MUSIC } from './music';

let ctx: AudioContext | null = null, master: GainNode | null = null, noiseBuf: AudioBuffer | null = null;
const last = new Map<string, number>();
function ac(): AudioContext | null {
  if (MUSIC.muted || !MUSIC.unlocked) return null;
  if (!ctx) {
    try { ctx = new AudioContext(); } catch { return null; }
    master = ctx.createGain(); master.gain.value = 0.5; master.connect(ctx.destination);
    noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const d = noiseBuf.getChannelData(0); for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}
function gate(k: string, ms: number) { const n = performance.now(); if ((last.get(k) ?? 0) + ms > n) return false; last.set(k, n); return true; }
function env(c: AudioContext, t0: number, a: number, att: number, dur: number): GainNode {
  const g = c.createGain(); g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(a, t0 + att); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur); g.connect(master!); return g;
}
function noise(c: AudioContext, dest: AudioNode, t0: number, dur: number, type: BiquadFilterType, f0: number, f1: number, q: number) {
  const s = c.createBufferSource(); s.buffer = noiseBuf; const f = c.createBiquadFilter(); f.type = type; f.Q.value = q;
  f.frequency.setValueAtTime(f0, t0); f.frequency.exponentialRampToValueAtTime(Math.max(30, f1), t0 + dur);
  s.connect(f).connect(dest); s.start(t0, Math.random()); s.stop(t0 + dur + 0.05);
}
function tone(c: AudioContext, dest: AudioNode, t0: number, dur: number, type: OscillatorType, f0: number, f1: number) {
  const o = c.createOscillator(); o.type = type; o.frequency.setValueAtTime(f0, t0); o.frequency.exponentialRampToValueAtTime(Math.max(30, f1), t0 + dur);
  o.connect(dest); o.start(t0); o.stop(t0 + dur + 0.02);
}

export const SFX_SURV = {
  /** the Breath: a breathy whisper, two or three syllables, quieter the less deep it is */
  whisper(depth = 1) {
    const c = ac(); if (!c || !gate('whisper', 4000)) return;
    const t = c.currentTime, n = 2 + Math.floor(Math.random() * 2);
    for (let i = 0; i < n; i++) {
      const t0 = t + i * (0.22 + Math.random() * 0.1), f = 1800 + Math.random() * 1600;
      const g = env(c, t0, 0.05 + 0.04 * Math.min(3, depth), 0.06, 0.26);
      const p = c.createStereoPanner(); p.pan.value = (Math.random() - 0.5) * 1.6; p.connect(g);
      noise(c, p, t0, 0.26, 'bandpass', f, f * 0.7, 5);
    }
  },
  /** radio: static, then a short squelch */
  crackle() {
    const c = ac(); if (!c || !gate('crackle', 1500)) return;
    const t = c.currentTime;
    noise(c, env(c, t, 0.12, 0.02, 0.45), t, 0.45, 'bandpass', 2400, 1200, 0.8);
    tone(c, env(c, t + 0.42, 0.05, 0.005, 0.08), t + 0.42, 0.08, 'square', 1100, 900);
  },
  /** a timed action is done */
  done() {
    const c = ac(); if (!c || !gate('done', 300)) return;
    const t = c.currentTime;
    tone(c, env(c, t, 0.15, 0.004, 0.09), t, 0.09, 'triangle', 880, 870);
    tone(c, env(c, t + 0.08, 0.12, 0.004, 0.12), t + 0.08, 0.12, 'triangle', 1320, 1300);
  },
  /** a moodle got worse */
  worse() {
    const c = ac(); if (!c || !gate('worse', 900)) return;
    const t = c.currentTime;
    tone(c, env(c, t, 0.1, 0.004, 0.12), t, 0.12, 'sine', 660, 650);
    tone(c, env(c, t + 0.11, 0.1, 0.004, 0.16), t + 0.11, 0.16, 'sine', 495, 490);
  },
};
